/* eslint-disable no-undef */
import { JwtHighbondClient } from '@acl-services/jwt-highbond-client';
import { useJwtHighbondClient } from './jwt-highbond-client';

// we need to create the XHR object here, because something overrides the original XHR later on,
// and that is buggy (most probably something inside JwtHighbondClient)
const xhr = new XMLHttpRequest();
xhr.withCredentials = true;

type Options = {
  headers?: Record<string, string>;
  body?: BodyInit;
  credentials?: RequestCredentials;
  method?: string;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function highbondFetch(
  url: string,
  hbClient: JwtHighbondClient,
  options: Options = {},
): Promise<Response> {
  const opts = {
    credentials: 'include' as RequestCredentials,
    ...options,
  };
  opts.headers = {
    Accept: 'application/json',
    ...(options.headers || {}),
  };
  if (opts.body) {
    if (typeof opts.body !== 'string') {
      opts.body = JSON.stringify(opts.body);
    }
    opts.headers['Content-Type'] = 'application/vnd.api+json';
  }
  try {
    return await hbClient.fetchWithTokenRefresh(url, opts);
  } catch (err) {
    return err as Response;
  }
}

async function highbondDownload(
  url: string,
  hbClient: JwtHighbondClient,
  options: Options = {},
): Promise<Response> {
  const opts = {
    credentials: 'include' as RequestCredentials,
    ...options,
  };
  opts.headers = {
    Accept: 'application/json',
    ...(options.headers || {}),
  };
  try {
    return hbClient.fetchWithTokenRefresh(url, opts);
  } catch (err) {
    return err as Response;
  }
}

async function fileUploadWithProgressXhr(
  url: string,
  hbClient: JwtHighbondClient,
  file: unknown,
  onUploadProgress: (loaded: number, total?: number) => void,
): Promise<Response> {
  function parseResponse() {
    return new Response(xhr.response);
  }

  return new Promise((resolve, reject) => {
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(parseResponse());
      } else if (xhr.status === 401) {
        retry();
      } else {
        reject(parseResponse());
      }
    };
    xhr.onerror = () => reject(parseResponse());
    xhr.onabort = () => reject(parseResponse());
    xhr.upload.onprogress = (event) =>
      onUploadProgress(event.loaded, event.total);

    function openAndSend() {
      xhr.open('POST', url);
      xhr.send(file as Document);
    }

    function retry() {
      hbClient['refreshToken']().then(openAndSend).catch(reject);
    }

    openAndSend();
  });
}

export function useHttp() {
  const client = useJwtHighbondClient();

  return {
    get: (url: string): Promise<Response> => highbondFetch(url, client),
    post: (url: string, body: Options['body']): Promise<Response> =>
      highbondFetch(url, client, {
        method: 'POST',
        body,
      }),
    fileUpload: (
      url: string,
      file: unknown,
      onUploadProgress: (loaded: number, total?: number) => void,
    ): Promise<Response> =>
      fileUploadWithProgressXhr(url, client, file, onUploadProgress),
    postDownload: (url: string, body: Options['body']): Promise<Response> =>
      highbondDownload(url, client, { method: 'POST', body }),
    put: (url: string, body: Options['body']): Promise<Response> =>
      highbondFetch(url, client, {
        method: 'PUT',
        body,
      }),
    patch: (
      url: string,
      body: Options['body'],
      options = {},
    ): Promise<Response> =>
      highbondFetch(url, client, {
        method: 'PATCH',
        body,
        ...options,
      }),
    delete: (url: string): Promise<Response> =>
      highbondFetch(url, client, {
        method: 'DELETE',
      }),
  };
}
